// ============================================================
// FILE: MasterDetailLayout.js
// LOCATION: C:\gardenmarket\web\src\components\MasterDetailLayout.js
// ============================================================
// PURPOSE:
//   Shared email-client-style layout: a list pane on the left
//   and a detail pane on the right.
//
// VISUAL REDESIGN (per product owner's mockup + spec):
//   - List rows no longer sit in a hard-bordered <table> with a
//     solid dark header. Header labels are now small, muted,
//     borderless caps text (matches cardHeader style used in the
//     detail pane), and rows are separated by a single light
//     divider line (colors.border) rather than full grid borders.
//   - Selected row gets a soft sage background tint + a thin
//     left-edge colour bar, instead of a heavy colored pill.
//   - Larger border radius (radius.lg) on the outer container,
//     soft layered shadow (shadow.soft) instead of a harsh
//     drop-shadow.
//   All values come from theme.js so this stays consistent with
//   the rest of the redesigned pages.
//
// PROPS: unchanged from previous version — see columns, renderRow,
//   renderDetail, etc. renderRow still returns a <tr> for table
//   semantics/accessibility, but the visual chrome around it is
//   now much lighter.
// ============================================================

import { useState, useEffect } from 'react';
import { colors, radius, shadow, space } from '../theme/theme';

const MOBILE_BREAKPOINT = 900;

export default function MasterDetailLayout({
    items,
    selectedId,
    onSelect,
    columns,
    renderRow,
    renderDetail,
    emptyDetailText = 'Select an item from the list to view details.',
    listHeader = null,
}) {
    // matchMedia rather than a resize listener: mobile browsers fire
    // resize every time the address bar hides or shows, which caused a
    // re-render of the whole list on every scroll gesture. matchMedia
    // only fires when the breakpoint is actually crossed.
    const [isMobile, setIsMobile] = useState(
        () => window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`).matches
    );

    useEffect(() => {
        const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
        const handler = (e) => setIsMobile(e.matches);
        setIsMobile(mq.matches);
        if (mq.addEventListener) mq.addEventListener('change', handler);
        else mq.addListener(handler);                    // Safari < 14
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', handler);
            else mq.removeListener(handler);
        };
    }, []);

    const hasSelection = selectedId !== null && selectedId !== undefined;

    const Table = () => (
        <table style={styles.table}>
            <thead>
                <tr>
                    {columns.map((col, i) => (
                        <th key={col} style={
                            i === 0 ? styles.thDate :
                            i === columns.length - 1 ? styles.thStatus :
                            styles.thJob
                        }>{col}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {items.map(item =>
                    renderRow(item, item.id === selectedId, () => onSelect(item.id))
                )}
            </tbody>
        </table>
    );

    // ── Mobile: show ONE pane at a time ──────────────────────
    if (isMobile) {
        if (hasSelection) {
            return (
                <div style={styles.mobileDetailWrap}>
                    <button onClick={() => onSelect(null)} style={styles.backBtn} className="gh-touch-target">
                        ← Back to list
                    </button>
                    {renderDetail()}
                </div>
            );
        }
        return (
            <div style={styles.mobileListWrap}>
                {listHeader}
                <Table />
            </div>
        );
    }

    // ── Desktop: side-by-side panes ───────────────────────────
    return (
        <div style={styles.desktopWrap}>
            <div style={styles.listPane}>
                {listHeader}
                <div style={styles.listCount}>{items.length} item{items.length !== 1 ? 's' : ''}</div>
                <div style={styles.tableScroll}>
                    <Table />
                    {items.length === 0 && (
                        <div style={styles.emptyList}>No items to show.</div>
                    )}
                </div>
            </div>
            <div style={styles.detailPane}>
                {hasSelection ? renderDetail() : (
                    <div style={styles.emptyDetail}>{emptyDetailText}</div>
                )}
            </div>
        </div>
    );
}

const styles = {
    desktopWrap:     { display:'grid', gridTemplateColumns:'420px 1fr', border:`1px solid ${colors.borderSoft}`, borderRadius: radius.lg, overflow:'hidden', minHeight:'500px', backgroundColor: colors.bgCard, boxShadow: shadow.soft },
    listPane:        { backgroundColor:'#fcfdfc', borderRight:`1px solid ${colors.border}`, display:'flex', flexDirection:'column' },
    listCount:       { padding:'6px 18px', fontSize:'0.72rem', color: colors.textFaint, borderBottom:`1px solid ${colors.border}` },
    tableScroll:     { overflowY:'auto', maxHeight:'72vh' },
    table:           { width:'100%', borderCollapse:'collapse' },
    th:              { textAlign:'left', padding:'10px 16px 6px', color: colors.textFaint, fontSize:'0.68rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.04em', whiteSpace:'nowrap', borderBottom:`1px solid ${colors.border}` },
    thDate:          { width:'56px', textAlign:'left', padding:'10px 8px 6px 16px', color: colors.textFaint, fontSize:'0.68rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.04em', borderBottom:`1px solid ${colors.border}` },
    thJob:           { textAlign:'left', padding:'10px 8px 6px', color: colors.textFaint, fontSize:'0.68rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.04em', borderBottom:`1px solid ${colors.border}` },
    thStatus:        { width:'80px', textAlign:'left', padding:'10px 16px 6px 8px', color: colors.textFaint, fontSize:'0.68rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.04em', borderBottom:`1px solid ${colors.border}` },
    emptyList:       { padding:'40px 16px', textAlign:'center', color: colors.textFaint, fontSize:'0.9rem' },
    detailPane:      { padding: space.lg, overflowY:'auto', maxHeight:'80vh', backgroundColor: colors.bgPage },
    emptyDetail:     { display:'flex', alignItems:'center', justifyContent:'center', height:'100%', color: colors.textFaint, fontSize:'0.95rem', minHeight:'300px' },
    mobileListWrap:  { backgroundColor: colors.bgCard, border:`1px solid ${colors.borderSoft}`, borderRadius: radius.lg, overflow:'hidden', boxShadow: shadow.soft },
    mobileDetailWrap:{ backgroundColor: colors.bgPage },
    backBtn:         { background:'none', border:'none', color: colors.brand, fontSize:'0.95rem', padding:'12px 14px', cursor:'pointer', fontWeight:'bold' },
};
